import React from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { withRouter, type RouteComponentProps } from "react-router";
import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { themeOptions } from "styles/theme";
import type { IStore } from "reducers";
import { toggleCartInsightsSidePanel, toggleGalleryInsightsSidePanel } from "actions/insights-actions";
import InsightsIcon from "components/icons/insights";
import { redirectWithWarning } from "actions/warning-actions";
import {
    isCurrencyCodeUSDSelector,
    isFirstLoadInsightsSelector,
    isInsightsButtonEnabledSelector,
    isLoadingInsightsSelector,
    isInsightsPermissionEnabled,
} from "reducers/insights-reducer";
import { fetchProductStyleInsights } from "actions/insights/product-style-insights";
import Globalize from "globalize";
import { InsightsActions } from "constants/analytics";
import { DATATEST } from "constants/integration";
import { fetchCategoryInsights } from "actions/insights/category-insights";
import { fetchProductColorInsights } from "actions/insights/product-color-insights";
import { fetchProductSizeInsights } from "actions/insights/product-size-insights";
import type { IProductSizeInsights } from "types/insights/product-size-insight";

type Page = "cart" | "gallery" | "other";

const isItemPage = (pathname: string) => pathname.includes("/item");

const getPage = (pathname): Page => {
    if (pathname.includes("cart")) {
        return "cart";
    } else if (pathname.includes("gallery")) {
        return "gallery";
    } else {
        return "other";
    }
};

interface StateProps {
    isInsightsPermissionEnabled: boolean;
    isCartInsightsSidePanelOpen: boolean;
    isGalleryInsightsSidePanelOpen: boolean;
    isButtonEnabled: boolean;
    isCurrencyUSD: boolean;
    cart: IStore["cart"];
    sizeInsights: IProductSizeInsights;
    scope: IStore["scope"];
    isLoadingInsights: boolean;
    isFirstLoadInsights: boolean;
}

interface DispatchProps {
    onToggleCartSidePanel: () => void;
    onToggleGallerySidePanel: () => void;
    onClosePDPDrawer: () => void;
    onFetchProductStyleInsights: (viewAsCompanyId?: string) => void;
    onFetchCategoryInsights: (viewAsCompanyId?: string) => void;
    onFetchProductColorInsights: (viewAsCompanyId?: string) => void;
    onFetchProductSizeInsights: (productIds) => void;
}

interface OwnProps {}

interface OwnPropsWithRouter extends OwnProps, RouteComponentProps<any, any> {}

const useStyles = makeStyles({
    tooltip: {
        backgroundColor: "#2D2D2D",
        color: "#F0F2F2",
        width: "234px",
        fontSize: "12px",
        padding: "4px 8px",
        borderRadius: "4px",
    },
    button: {
        borderRadius: "2px !important",
        width: "44px",
        minWidth: "44px",
        height: "40px",
        padding: 0,
    },
    loadingInsightsButton: {
        position: "relative",
        paddingBottom: "10px",
        marginLeft: "14px",
        marginRight: "18px",
        paddingTop: "13px",
        transition: "padding-top 0.3s ease",
    },
});

/**
 * The button to toggle the insights side panel is used in the site header.
 * It is only shown when the order insights feature flag is enabled.
 * The button toggles the insights side panel for the current page and should be visible only for the cart and gallery pages.
 */
const InsightsSidePanelToggleButton: React.FC<OwnPropsWithRouter & StateProps & DispatchProps> = ({
    isInsightsPermissionEnabled,
    isCartInsightsSidePanelOpen,
    isGalleryInsightsSidePanelOpen,
    onToggleCartSidePanel,
    onToggleGallerySidePanel,
    location,
    onClosePDPDrawer,
    isButtonEnabled,
    isCurrencyUSD,
    onFetchProductStyleInsights,
    cart,
    sizeInsights,
    onFetchCategoryInsights,
    onFetchProductColorInsights,
    onFetchProductSizeInsights,
    scope,
    isLoadingInsights,
    isFirstLoadInsights,
}) => {
    const classes = useStyles();

    const insightsFetchingAllowed = React.useMemo(() => {
        return isInsightsPermissionEnabled && isCurrencyUSD;
    }, [isInsightsPermissionEnabled, isCurrencyUSD]);

    const [currentViewAsId, setCurrentViewAsId] = React.useState(scope.viewAs?._id);

    const showSpinner = isLoadingInsights || (isFirstLoadInsights && insightsFetchingAllowed);

    const cartItemIds = React.useMemo(() => {
        return cart.items ? (Array.from(cart.items.keys()) as string[]) : [];
    }, [cart.items]);

    React.useEffect(
        () => {
            if (
                getPage(location.pathname) === "cart" &&
                !isFirstLoadInsights &&
                currentViewAsId === scope.viewAs?._id
            ) {
                return;
            }

            if (insightsFetchingAllowed) {
                onFetchProductStyleInsights(scope.viewAs?._id);
                onFetchCategoryInsights(scope.viewAs?._id);
                onFetchProductColorInsights(scope.viewAs?._id);
            }
            setCurrentViewAsId(scope.viewAs?._id);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [insightsFetchingAllowed, scope.viewAs],
    );

    React.useEffect(() => {
        const fetchedProductIds = Object.keys(sizeInsights);
        const cartHasUnfetchedItems = cartItemIds.some((id) => !fetchedProductIds.includes(id));
        if (insightsFetchingAllowed && getPage(location.pathname) === "cart" && cartHasUnfetchedItems) {
            onFetchProductSizeInsights(cartItemIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insightsFetchingAllowed, location.pathname, cart.items]);

    if (isInsightsPermissionEnabled) {
        if (showSpinner) {
            return (
                <div className={classes.loadingInsightsButton}>
                    <CircularProgress
                        size={16}
                        thickness={1}
                        data-test={DATATEST.INSIGHTS.TOGGLE_BUTTON_LOADING_SPINNER}
                    />
                </div>
            );
        }

        const page = getPage(location.pathname);

        let isInsightsSidePanelOpen = false;
        if (page === "cart") {
            isInsightsSidePanelOpen = isCartInsightsSidePanelOpen;
        } else if (page === "gallery") {
            isInsightsSidePanelOpen = isGalleryInsightsSidePanelOpen;
        }

        const onToggleInsightsSidePanel = () => {
            if (page === "cart") {
                if (!isInsightsSidePanelOpen) {
                    global.analytics.track(InsightsActions.openDrawerCart);
                }
                onToggleCartSidePanel();
            } else if (page === "gallery") {
                if (!isInsightsSidePanelOpen) {
                    global.analytics.track(InsightsActions.openDrawerGallery);
                }
                onToggleGallerySidePanel();
                if (isItemPage(location.pathname)) {
                    // if the feature flag for the new PDP for order insights is enabled
                    // Then close the PDP drawer
                    onClosePDPDrawer();
                }
            }
        };

        let tooltipText = "";
        if (!isCurrencyUSD) {
            tooltipText = Globalize.formatMessage("Order Trends are available in USD only");
        } else if (!isButtonEnabled) {
            tooltipText = Globalize.formatMessage(
                "Order Trends are only available for brands with sufficient data in USD orders.",
            );
        }

        return (
            <Tooltip title={tooltipText} placement="bottom" classes={{ tooltip: classes.tooltip }}>
                <span>
                    <Button
                        className={classes.button}
                        onClick={onToggleInsightsSidePanel}
                        disabled={!isButtonEnabled}
                        data-test={DATATEST.INSIGHTS.TOGGLE_BUTTON}
                    >
                        <InsightsIcon
                            fontSize="large"
                            data-test={DATATEST.INSIGHTS.TOGGLE_BUTTON_ICON}
                            htmlColor={
                                isButtonEnabled && isInsightsSidePanelOpen
                                    ? themeOptions.palette.primary1Color
                                    : themeOptions.palette.grey
                            }
                        />
                    </Button>
                </span>
            </Tooltip>
        );
    } else {
        return null;
    }
};

const mapStateToProps = (state: IStore, ownProps: OwnPropsWithRouter) => ({
    isCartInsightsSidePanelOpen: state.insights.cartInsightsSidePanelOpen,
    isGalleryInsightsSidePanelOpen: state.insights.galleryInsightsSidePanelOpen,
    isOrderInsightsEnabled: state.ldFlags.orderInsightsNu64953,
    isInsightsPermissionEnabled: isInsightsPermissionEnabled(state),
    isButtonEnabled: isInsightsButtonEnabledSelector(state, ownProps.location.pathname),
    isCurrencyUSD: isCurrencyCodeUSDSelector(state),
    sizeInsights: state.insights.productSizes,
    isLoadingInsights: isLoadingInsightsSelector(state, ownProps.location.pathname),
    isFirstLoadInsights: isFirstLoadInsightsSelector(state, ownProps.location.pathname),
    cart: state.cart,
    scope: state.scope,
});

const mapDispatchToProps = (dispatch, ownProps: OwnPropsWithRouter) => ({
    onToggleCartSidePanel: () => dispatch(toggleCartInsightsSidePanel()),
    onToggleGallerySidePanel: () => dispatch(toggleGalleryInsightsSidePanel()),
    onFetchProductStyleInsights: (companyId) => dispatch(fetchProductStyleInsights(companyId)),
    onClosePDPDrawer: () => {
        const { location } = ownProps;
        dispatch(
            redirectWithWarning({
                pathname: _(location.pathname).split("/").slice(0, -2).join("/"),
                query: location.query && _.omit(location.query, ["prebook", "shipStart", "shipEnd", "orderType"]),
            }),
        );
    },
    onFetchCategoryInsights: (companyId) => dispatch(fetchCategoryInsights(companyId)),
    onFetchProductColorInsights: (companyId) => dispatch(fetchProductColorInsights(companyId)),
    onFetchProductSizeInsights: (productIds) => dispatch(fetchProductSizeInsights(productIds)),
});

export default withRouter<OwnProps>(
    connect<StateProps, DispatchProps, OwnPropsWithRouter>(
        mapStateToProps,
        mapDispatchToProps,
    )(InsightsSidePanelToggleButton),
);
